import QRCodeGenerator from 'qrcode'
import { useEffect, useState } from 'react'

const ETHEREUM_ADDRESS = /^0x[0-9a-fA-F]{40}$/
const TRON_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/

// At most 8 decimals and never the exponent form (`1e-7`), trailing zeros cut.
const btcAmount = (amount: number) =>
  amount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')

// The text of the deposit QR, or null where no wallet reads it for sure. A memo
// / tag does not fit in the code, so a payer who scans it would miss the tag.
// ETH, USDT and USDC carry the bare address: an amount needs the wei value and
// the token contract, which the cabinet does not have, and Tron has no common
// URI.
export const cryptoQrText = (
  address: string | null | undefined,
  amount: number | null | undefined,
  currency: string | undefined,
  memo?: string | null
): string | null => {
  if (!address || memo) {
    return null
  }

  if (currency === 'BTC') {
    // The form gives '0' for a zero; Number() reads a string amount too.
    const value = Number(amount)

    return value > 0
      ? `bitcoin:${address}?amount=${btcAmount(value)}`
      : `bitcoin:${address}`
  }

  if (currency === 'ETH' && ETHEREUM_ADDRESS.test(address)) {
    return address
  }

  if (
    (currency === 'USDT' || currency === 'USDC') &&
    (ETHEREUM_ADDRESS.test(address) || TRON_ADDRESS.test(address))
  ) {
    return address
  }

  return null
}

// The data URL of the deposit QR, or '' when there is no code to show.
export const useCryptoQr = (
  address: string | null | undefined,
  amount: number | null | undefined,
  currency: string | undefined,
  memo?: string | null
): string => {
  const [qrCode, setQrCode] = useState({ text: '', src: '' })

  const text = cryptoQrText(address, amount, currency, memo)

  useEffect(() => {
    if (!text) {
      return
    }

    let cancelled = false

    QRCodeGenerator.toDataURL(text, {
      color: { light: '#00000000' },
      margin: 0,
    })
      .then(src => {
        if (!cancelled) {
          setQrCode({ text, src })
        }
      })
      // No code is drawn: the hook keeps returning ''.
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [text])

  // The code of an older text is never shown, not even for one render.
  return text && qrCode.text === text ? qrCode.src : ''
}
